import React, { useMemo } from "react";
import { ErpIntegrationInvoiceResource } from "Shared/util/types";
import { formatDate, formatAmount } from "Shared/util/formatters";
import DataTable, { ColumnTypes } from "Shared/components/tables/data-table";
import { useTranslation } from "Shared/util/custom-hooks";
import { translateColumns } from "Shared/util/translations";
import styles from "./invoice-integration-page-table.css";

const columns = [
    {
        title: "invoiceNumber",
        key: "invoiceId",
    },
    {
        title: "customer",
        key: "customerName",
    },
    {
        title: "invoiceDate",
        key: "invoiceDate",
        type: ColumnTypes.Date,
        formatter: (d: string) => formatDate(d),
        hiddenMobile: true,
    },
    {
        title: "dueDate",
        key: "invoiceDueDate",
        type: ColumnTypes.Date,
        formatter: (d: string) => formatDate(d),
        hiddenMobile: true,
    },
    {
        title: "totalAmount",
        key: "amount",
        type: ColumnTypes.Amount,
        formatter: (d: number) => formatAmount(d),
        footer: {
            sum: true,
        },
    },
    {
        title: "currency",
        key: "currency",
        footer: {
            groupBy: true,
        },
    },
    {
        title: "errorMessage",
        key: "errorMessage",
        oneLineEllipsis: true,
        hiddenMobile: true,
    },
];

type InvoiceIntegrationPageTableProps = {
    providerName: string;
    loading: boolean;
    loadingTopText: string;
    loadingBottomText: string;
    rows: ErpIntegrationInvoiceResource[];
    onSelectedRowsChanged: (
        selectedRows: ErpIntegrationInvoiceResource[],
    ) => void;
    preSelectedRows: boolean;
};

const InvoiceIntegrationPageTable = ({
    providerName,
    loading,
    loadingTopText,
    loadingBottomText,
    rows,
    onSelectedRowsChanged,
    preSelectedRows,
}: InvoiceIntegrationPageTableProps) => {
    const t = useTranslation((x) => x.invoiceIntegration);
    const tg = useTranslation((x) => x.global);

    const showRowInformationColumn = useMemo(
        () => rows.some((row) => row.errorDescriptions.length > 0),
        [rows],
    );

    const rowDisabled = (row: ErpIntegrationInvoiceResource) =>
        row && (row.errorDescriptions.length > 0 || loading);

    const rowInformationMessage = (row: ErpIntegrationInvoiceResource) =>
        row.errorDescriptions &&
        row.errorDescriptions.length > 0 && (
            <div>
                <h5 className={styles.tooltipTitle}>
                    {t.format(t.theFollowingErrorsMustBeFixedIn, {
                        serviceProvider: providerName,
                    })}
                </h5>
                {row.errorDescriptions.map((desc: string, i: number) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div className={styles.tooltipMessageContainer} key={i}>
                        <span>•</span>
                        <span>{desc}</span>
                    </div>
                ))}
                {row.electronic && (
                    <div className={styles.tooltipDisclaimer}>
                        {t.yourCustomerWantsAnEinvoice}
                    </div>
                )}
            </div>
        );

    return (
        <DataTable
            loading={loading}
            loadingTopText={loadingTopText}
            loadingBottomText={loadingBottomText}
            columns={translateColumns(columns, tg)}
            rows={rows}
            paging={false}
            useSelectableRows
            selectableRowHidden={rowDisabled}
            onSelectedRowsChanged={onSelectedRowsChanged}
            preSelectedRows={preSelectedRows}
            useRowInformation={showRowInformationColumn}
            rowInformationMessage={rowInformationMessage}
        />
    );
};

export default InvoiceIntegrationPageTable;
